import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { EditorState } from '@tiptap/pm/state';
import {
  REVIEW_META,
  adoptBaselineBlockIds,
  buildFinalizeTransaction,
  buildResolveTransaction,
  collectReviewChanges,
  docHasReviewMarkup,
  docToHtml,
  dropTrailingReviewArtifact,
  getReviewBaselineDoc,
  parseReviewHtml,
} from './reviewEngine';
import { setEditorEditable } from './useEditorEditable';
import { createBlockId } from '../../extensions/BlockId';

let reviewCounter = 0;

/**
 * Give every settled top-level block (no pending review state) that lacks a block id a fresh
 * one, inside `tr`. BlockId does not stamp while a review is open, so blocks a user accepts one
 * by one would otherwise stay unaddressable (getBlocks reports them without id and the server
 * drops them) until the whole review ends. Pending blocks are left alone; ids that blocks share
 * on purpose during review (a replacement and its deleted original) are not touched.
 * @param {import('@tiptap/pm/state').Transaction} tr - Transaction to extend.
 * @returns {void}
 */
export const stampSettledBlockIds = (tr) => {
  const taken = new Set();
  const missing = [];
  tr.doc.forEach((node, offset) => {
    if (!Object.prototype.hasOwnProperty.call(node.attrs, 'blockId')) return;
    if (node.attrs.blockId) taken.add(node.attrs.blockId);
    else if (!node.attrs.diffBlock && !docHasReviewMarkup(node)) missing.push(offset);
  });
  missing.forEach((offset) => {
    const id = createBlockId(taken);
    taken.add(id);
    tr.setNodeAttribute(offset, 'blockId', id);
  });
};

/**
 * Create a review id.
 * @returns {string} Id such as `rv_3_lq2x9`.
 */
const createReviewId = () => {
  reviewCounter += 1;
  return `rv_${reviewCounter}_${Date.now().toString(36)}`;
};

/**
 * Owns AI review state for one editor: entering a review (from HTML or from a prepared
 * document), per-change accept/reject on the ProseMirror document, and finishing the review
 * as a single undo step on top of the pre-review history.
 *
 * @param {import('@tiptap/core').Editor|null} editor - Editor instance.
 * @param {{readOnlyRef: React.MutableRefObject<boolean>, emit: (event: string, payload: object) => void, beforeEnter?: () => void}} options - Host hooks.
 * @returns {{review: object|null, controller: object}} Review UI state and a stable controller.
 */
export default function useReviewController(editor, { readOnlyRef, emit, beforeEnter }) {
  const [review, setReview] = useState(null);
  const sessionRef = useRef(null);
  const emitRef = useRef(emit);
  const beforeEnterRef = useRef(beforeEnter);
  emitRef.current = emit;
  beforeEnterRef.current = beforeEnter;

  /**
   * Publish the current change list to React state.
   * @param {number} [activeIndex] - Preferred active index.
   * @returns {Array<object>} Changes.
   */
  const publish = useCallback((activeIndex) => {
    const session = sessionRef.current;
    if (!session || !editor) return [];
    const changes = collectReviewChanges(editor.state.doc);
    session.changes = changes;
    setReview((prev) => {
      const index = typeof activeIndex === 'number' ? activeIndex : (prev ? prev.activeIndex : 0);
      return {
        reviewId: session.reviewId,
        label: session.label,
        changes,
        activeIndex: Math.max(0, Math.min(index, changes.length - 1)),
        version: (prev ? prev.version : 0) + 1,
      };
    });
    return changes;
  }, [editor]);

  /**
   * Finish the review: restore the pre-review state and apply `resolvedDoc` as one step.
   * @param {import('@tiptap/pm/model').Node} resolvedDoc - Document to keep.
   * @param {'accepted'|'rejected'|'mixed'|'cancelled'} outcome - Outcome label.
   * @returns {void}
   */
  const finalize = useCallback((resolvedDoc, outcome) => {
    const session = sessionRef.current;
    if (!session || !editor || editor.isDestroyed) return;
    sessionRef.current = null;
    if (editor.storage.quillRuntime) editor.storage.quillRuntime.reviewActive = false;
    const tr = buildFinalizeTransaction(editor.view, session.savedState, resolvedDoc);
    setEditorEditable(editor, !readOnlyRef.current);
    editor.view.dispatch(tr);
    setReview(null);
    emitRef.current('review-resolved', {
      reviewId: session.reviewId,
      outcome,
      accepted: session.accepted,
      rejected: session.rejected,
      html: editor.getHTML(),
    });
  }, [editor, readOnlyRef]);

  const controller = useMemo(() => {
    /**
     * Whether a review is active.
     * @returns {boolean} True while reviewing.
     */
    const isActive = () => Boolean(sessionRef.current);

    /**
     * Review baseline (pending changes rejected) without editor-added artifacts.
     * @param {{savedState: import('@tiptap/pm/state').EditorState}} session - Active session.
     * @returns {import('@tiptap/pm/model').Node} Baseline document.
     */
    const baselineOf = (session) => dropTrailingReviewArtifact(getReviewBaselineDoc(editor.state), session.savedState.doc);

    /**
     * Enter (or replace) a review with the given review document.
     * @param {import('@tiptap/pm/model').Node} reviewDoc - Document with diff marks/attrs.
     * @param {{label?: string, origin?: string, tr?: import('@tiptap/pm/state').Transaction}} [opts] - Label, origin (who proposed
     *   the changes, e.g. 'leaked' or the caller's label; defaults to the label) and an optional prepared transaction on the live state.
     * @returns {{reviewId: string|null, changes: Array<object>}} Result.
     */
    const enterWithDoc = (reviewDoc, opts = {}) => {
      if (!editor || editor.isDestroyed) return { reviewId: null, changes: [] };
      const changes = collectReviewChanges(reviewDoc);
      if (changes.length === 0) return { reviewId: null, changes };
      if (!sessionRef.current) beforeEnterRef.current?.();
      const previous = sessionRef.current;
      const session = {
        reviewId: createReviewId(),
        label: opts.label || 'AI changes',
        origin: String(opts.origin || opts.label || 'AI changes'),
        savedState: previous ? previous.savedState : editor.state,
        accepted: previous ? previous.accepted : 0,
        rejected: previous ? previous.rejected : 0,
        changes,
      };
      sessionRef.current = session;
      if (editor.storage.quillRuntime) editor.storage.quillRuntime.reviewActive = true;
      setEditorEditable(editor, false);
      const tr = opts.tr && !previous
        ? opts.tr
        : editor.state.tr.replaceWith(0, editor.state.doc.content.size, reviewDoc.content);
      tr.setMeta('addToHistory', false);
      tr.setMeta('preventUpdate', true);
      tr.setMeta(REVIEW_META, 'enter');
      editor.view.dispatch(tr);
      publish(0);
      if (previous) emitRef.current('review-resolved', { reviewId: previous.reviewId, outcome: 'superseded', accepted: previous.accepted, rejected: previous.rejected });
      emitRef.current('review-started', { reviewId: session.reviewId, label: session.label, changes });
      return { reviewId: session.reviewId, changes };
    };

    /**
     * Enter a review from merged HTML containing diff spans. The HTML carries no block ids,
     * so the ids of the current baseline are adopted (see adoptBaselineBlockIds): rejecting
     * everything then leaves the document, and every remembered block id, untouched.
     * @param {string} mergedHtml - Review HTML.
     * @param {{label?: string, origin?: string}} [opts] - Options.
     * @returns {{reviewId: string|null, changes: Array<object>}} Result.
     */
    const enterWithHtml = (mergedHtml, opts = {}) => {
      if (!editor || editor.isDestroyed) return { reviewId: null, changes: [] };
      const baseDoc = sessionRef.current ? baselineOf(sessionRef.current) : editor.state.doc;
      return enterWithDoc(adoptBaselineBlockIds(parseReviewHtml(mergedHtml, editor.schema), baseDoc), opts);
    };

    /**
     * Resolve some or all changes.
     * @param {Set<string>|null} ids - Change ids or null for all.
     * @param {'accept'|'reject'} action - Resolution.
     * @returns {boolean} Whether anything was resolved.
     */
    const resolve = (ids, action) => {
      const session = sessionRef.current;
      if (!session || !editor || editor.isDestroyed) return false;
      const count = ids ? ids.size : session.changes.length;
      if (action === 'accept') session.accepted += count;
      else session.rejected += count;
      if (!ids) {
        const resolvedDoc = buildResolveTransaction(editor.state, null, action).doc;
        let outcome = action === 'accept' ? 'accepted' : 'rejected';
        if (session.accepted && session.rejected) outcome = 'mixed';
        finalize(resolvedDoc, outcome);
        return true;
      }
      const tr = buildResolveTransaction(editor.state, ids, action);
      stampSettledBlockIds(tr);
      tr.setMeta('preventUpdate', true);
      editor.view.dispatch(tr);
      const remaining = collectReviewChanges(editor.state.doc);
      if (remaining.length === 0) {
        let outcome = session.rejected ? 'rejected' : 'accepted';
        if (session.accepted && session.rejected) outcome = 'mixed';
        finalize(editor.state.doc, outcome);
      } else {
        publish();
      }
      return true;
    };

    return {
      isActive,
      enterWithDoc,
      enterWithHtml,
      /**
       * Finish the review keeping `doc` (e.g. a direct edit applied to the baseline).
       * @param {import('@tiptap/pm/model').Node} doc - Document to keep.
       * @returns {void}
       */
      commitDoc: (doc) => finalize(doc, 'superseded'),
      acceptChange: (id) => resolve(new Set([String(id)]), 'accept'),
      rejectChange: (id) => resolve(new Set([String(id)]), 'reject'),
      acceptAll: () => resolve(null, 'accept'),
      rejectAll: () => resolve(null, 'reject'),
      /**
       * Current review baseline (pending changes rejected), or the live doc outside review.
       * @returns {import('@tiptap/pm/model').Node|null} Document.
       */
      getBaselineDoc: () => {
        if (!editor || editor.isDestroyed) return null;
        return sessionRef.current ? baselineOf(sessionRef.current) : editor.state.doc;
      },
      /**
       * Baseline HTML (see getBaselineDoc).
       * @returns {string} HTML.
       */
      getBaselineHtml: () => {
        if (!editor || editor.isDestroyed) return '';
        if (!sessionRef.current) return editor.getHTML();
        return docToHtml(baselineOf(sessionRef.current), editor.schema);
      },
      /**
       * A throwaway state holding the baseline document (for planning edits during review).
       * @returns {EditorState|null} State.
       */
      getBaselineState: () => {
        if (!editor || editor.isDestroyed) return null;
        if (!sessionRef.current) return editor.state;
        return EditorState.create({ doc: baselineOf(sessionRef.current), schema: editor.schema });
      },
      /**
       * Pending review summary.
       * @returns {{reviewId: string, label: string, origin: string, changes: Array<{id: string, kind: string, summary: string}>}|null} Review.
       */
      getPending: () => {
        const session = sessionRef.current;
        if (!session) return null;
        return {
          reviewId: session.reviewId,
          label: session.label,
          origin: session.origin,
          changes: session.changes.map(({ id, kind, summary }) => ({ id, kind, summary })),
        };
      },
      /**
       * Drop the review without applying anything (used before loading new content).
       * @returns {void}
       */
      discard: () => {
        const session = sessionRef.current;
        if (!session) return;
        sessionRef.current = null;
        if (editor && editor.storage.quillRuntime) editor.storage.quillRuntime.reviewActive = false;
        setReview(null);
        emitRef.current('review-resolved', { reviewId: session.reviewId, outcome: 'cancelled', accepted: session.accepted, rejected: session.rejected });
      },
    };
  }, [editor, finalize, publish]);

  // A destroyed editor cannot hold a review.
  useEffect(() => () => {
    sessionRef.current = null;
  }, [editor]);

  return { review, setActiveIndex: (index) => setReview((prev) => (prev ? { ...prev, activeIndex: index } : prev)), controller };
}
